/**
 * «Своя стратегия» — именованные пресеты пользователя с настоящими ползунками
 * (config/custom-strategy.ts, PLAN STEP 17, решение заказчика 2026-09-18).
 *
 * Редактор: чипсы «Мои стратегии» (+ создать), имя пресета, девять
 * `<input type="range">` по спекам из /api/settings, механика серии чипсами,
 * индикаторы галочками, фильтр новостей тумблером. Правки живут в черновике
 * и уходят на сервер кнопкой «Сохранить»; «Выбрать» делает пресет активной
 * стратегией. Сессия (запуск, опрос, сделки) — общая с автотрейдингом: панель
 * AutotradingPanel получает редактор и подписи через `custom`.
 */
import { useEffect, useState } from 'react';
import { AutotradingPanel } from './AutotradingPanel.js';
import { SheetPortal } from './SheetPortal.js';
import { ApiError, api, apiMessage } from '../lib/api.js';
import type {
  CustomOptions,
  CustomSliderKey,
  CustomSliderSpec,
  CustomStrategyParams,
  SettingsPayload,
  UserStrategy,
} from '../lib/api.js';
import { haptic } from '../lib/telegram.js';
import { useApp } from '../state/app.js';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** Спеки на случай старого API без раздела custom — те же, что в конфиге. */
const FALLBACK_OPTIONS: CustomOptions = {
  sliders: [
    { key: 'stakePercent', min: 0.5, max: 10, step: 0.5, default: 2 },
    { key: 'progression', min: 1, max: 3, step: 0.1, default: 2 },
    { key: 'maxSteps', min: 1, max: 10, step: 1, default: 3 },
    { key: 'seriesCount', min: 1, max: 20, step: 1, default: 5 },
    { key: 'timeframeSeconds', min: 0, max: 300, step: 5, default: 30, steps: [0, 5, 15, 30, 60, 180, 300] },
    { key: 'pauseSeconds', min: 0, max: 180, step: 10, default: 30 },
    { key: 'durationMinutes', min: 15, max: 240, step: 15, default: 60 },
    { key: 'stopLossPercent', min: 0, max: 50, step: 5, default: 0 },
    { key: 'takeProfitPercent', min: 0, max: 100, step: 5, default: 0 },
  ],
  modes: ['recovery', 'flat', 'paroli', 'streak'],
  indicators: [],
  newsWindows: [],
  limits: { maxPresets: 10, nameMinLength: 1, nameMaxLength: 24 },
  seriesCost: {},
};

function timeframeLabel(t: Translate, seconds: number): string {
  if (seconds === 0) return t('atr.value.random');
  return seconds < 60
    ? t('atr.timeframe.seconds', { value: seconds })
    : t('atr.timeframe.minutes', { value: Math.round(seconds / 60) });
}

function limitLabel(t: Translate, percent: number): string {
  return percent === 0 ? t('atr.value.off') : t('atr.percent', { value: percent });
}

function valueLabel(t: Translate, key: CustomSliderKey, value: number): string {
  switch (key) {
    case 'stakePercent':
      return `${value} %`;
    case 'progression':
      return `×${value.toFixed(1)}`;
    case 'timeframeSeconds':
      return timeframeLabel(t, value);
    case 'pauseSeconds':
      return t('atr.timeframe.seconds', { value });
    case 'durationMinutes':
      return t('app.atr.minutes', { value });
    case 'stopLossPercent':
    case 'takeProfitPercent':
      return limitLabel(t, value);
    default:
      return String(value);
  }
}

/** Значение с ползунка → сетка шага (как `snapSlider` в конфиге, без хвостов плавающей точки). */
function snap(spec: CustomSliderSpec, value: number): number {
  if (spec.steps) {
    return spec.steps.reduce((best, step) => (Math.abs(step - value) < Math.abs(best - value) ? step : best));
  }
  const clamped = Math.min(spec.max, Math.max(spec.min, value));
  const decimals = (String(spec.step).split('.')[1] ?? '').length;
  const factor = 10 ** decimals;
  return Math.round((spec.min + Math.round((clamped - spec.min) / spec.step) * spec.step) * factor) / factor;
}

function Slider({
  spec,
  value,
  label,
  display,
  onChange,
}: {
  spec: CustomSliderSpec;
  value: number;
  label: string;
  display: string;
  onChange: (value: number) => void;
}) {
  const steps = spec.steps;
  const index = steps ? Math.max(0, steps.indexOf(value)) : null;
  const min = steps ? 0 : spec.min;
  const max = steps ? steps.length - 1 : spec.max;
  const current = steps ? (index as number) : value;
  const fill = max > min ? ((current - min) / (max - min)) * 100 : 0;
  return (
    <div className="cst-slider">
      <div className="row-between">
        <span className="label">{label}</span>
        <span className="cst-slider-value">{display}</span>
      </div>
      <input
        type="range"
        className="cst-range"
        style={{ '--fill': `${fill}%` } as React.CSSProperties}
        min={min}
        max={max}
        step={steps ? 1 : spec.step}
        value={current}
        aria-label={label}
        onChange={(event) => {
          const raw = Number(event.target.value);
          onChange(steps ? (steps[raw] ?? value) : snap(spec, raw));
        }}
      />
    </div>
  );
}

export function CustomStrategyPanel({
  settings,
  onSettings,
  brandName,
}: {
  settings: SettingsPayload;
  onSettings: (next: SettingsPayload) => void;
  brandName: string;
}) {
  const { showToast, t } = useApp();
  const options = settings.options.custom ?? FALLBACK_OPTIONS;
  const selectedId = settings.current.customStrategyId;

  const [list, setList] = useState<UserStrategy[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CustomStrategyParams | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [nameSheet, setNameSheet] = useState<'create' | 'rename' | null>(null);
  const [nameValue, setNameValue] = useState('');

  const editing = list.find((item) => item.id === editingId) ?? null;
  const params = draft;

  const open = (strategy: UserStrategy): void => {
    setEditingId(strategy.id);
    setDraft({ ...strategy.params, indicators: [...strategy.params.indicators] });
    setDirty(false);
    setConfirmDelete(false);
  };

  /** Список пресетов — один раз при открытии; редактируем выбранный или первый. */
  useEffect(() => {
    void api
      .strategies()
      .then(({ strategies, selectedId: serverSelected }) => {
        setList(strategies);
        const first = strategies.find((item) => item.id === (serverSelected ?? selectedId)) ?? strategies[0];
        if (first) open(first);
        // сервер знает выбранный точнее (мог быть удалён из бота)
        if (serverSelected !== selectedId) {
          onSettings({ ...settings, current: { ...settings.current, customStrategyId: serverSelected } });
        }
      })
      .catch((err: unknown) => showToast(apiMessage(err, t, 'app.error.load_failed')))
      .finally(() => setLoaded(true));
    // один раз при монтировании: дальше состояние ведёт сам редактор
  }, []);

  const setParam = <K extends keyof CustomStrategyParams>(key: K, value: CustomStrategyParams[K]): void => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  };

  const toggleIndicator = (id: string): void => {
    if (!params) return;
    haptic();
    const next = params.indicators.includes(id)
      ? params.indicators.filter((item) => item !== id)
      : [...params.indicators, id];
    // порядок — как в списке индикаторов, как и на сервере
    setParam(
      'indicators',
      options.indicators.map((item) => item.id).filter((item) => next.includes(item)),
    );
  };

  const replaceInList = (strategy: UserStrategy): void =>
    setList((prev) => prev.map((item) => (item.id === strategy.id ? strategy : item)));

  const nameError = (err: unknown): string => {
    const payload = err instanceof ApiError ? (err.payload as { error?: string; reason?: string; limit?: number } | null) : null;
    if (payload?.error === 'strategy_name') {
      return t(payload.reason === 'taken' ? 'app.cst.name_taken' : 'app.cst.name_invalid');
    }
    if (payload?.error === 'strategy_limit') return t('app.cst.limit', { limit: payload.limit ?? options.limits.maxPresets });
    return apiMessage(err, t, 'app.account.save_failed');
  };

  async function save(): Promise<UserStrategy | null> {
    if (!editing || !params) return null;
    setBusy(true);
    try {
      const saved = await api.updateStrategy(editing.id, { params });
      replaceInList(saved);
      setDraft({ ...saved.params, indicators: [...saved.params.indicators] });
      setDirty(false);
      showToast(t('app.cst.saved'));
      return saved;
    } catch (err) {
      showToast(apiMessage(err, t, 'app.account.save_failed'));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function select(): Promise<void> {
    if (!editing) return;
    haptic('medium');
    if (dirty && !(await save())) return;
    setBusy(true);
    try {
      const current = await api.selectStrategy(editing.id);
      onSettings({ ...settings, current: { ...settings.current, ...current, strategy: 'custom', customStrategyId: editing.id } });
      showToast(t('toast.cst.selected', { name: editing.name }));
    } catch (err) {
      showToast(apiMessage(err, t, 'app.account.save_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function submitName(): Promise<void> {
    const name = nameValue.trim();
    if (!name) return;
    setBusy(true);
    try {
      if (nameSheet === 'rename' && editing) {
        const renamed = await api.updateStrategy(editing.id, { name });
        replaceInList(renamed);
      } else {
        const created = await api.createStrategy(name);
        setList((prev) => [...prev, created]);
        open(created);
      }
      setNameSheet(null);
      setNameValue('');
    } catch (err) {
      showToast(nameError(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!editing) return;
    setBusy(true);
    try {
      await api.deleteStrategy(editing.id);
      const rest = list.filter((item) => item.id !== editing.id);
      setList(rest);
      setConfirmDelete(false);
      if (selectedId === editing.id) {
        onSettings({ ...settings, current: { ...settings.current, customStrategyId: null } });
      }
      const next = rest[0];
      if (next) open(next);
      else {
        setEditingId(null);
        setDraft(null);
      }
    } catch (err) {
      showToast(apiMessage(err, t, 'app.account.save_failed'));
    } finally {
      setBusy(false);
    }
  }

  const canCreate = list.length < options.limits.maxPresets;
  const seriesCost = params ? (options.seriesCost[String(params.maxSteps)] ?? 0) : 0;

  const editor = (
    <div className="cst-editor">
      {/* мои стратегии: чипсы, ✓ — выбранная, подсветка — та, что редактируем */}
      <div>
        <div className="label">{t('app.cst.my')}</div>
        <div className="chip-grid" style={{ '--chip-cols': 2 } as React.CSSProperties}>
          {list.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`chip chip-strategy ${item.id === editingId ? 'is-active' : ''}`}
              onClick={() => {
                haptic();
                open(item);
              }}
            >
              <span className="chip-label">
                {item.id === selectedId ? '✓ ' : ''}
                {item.name}
              </span>
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              className="chip"
              onClick={() => {
                haptic();
                setNameValue('');
                setNameSheet('create');
              }}
            >
              <span className="chip-label">＋ {t('app.cst.new')}</span>
            </button>
          )}
        </div>
        {loaded && list.length === 0 && <p className="step-hint">{t('app.cst.empty')}</p>}
        {!canCreate && <p className="step-hint">{t('app.cst.limit', { limit: options.limits.maxPresets })}</p>}
      </div>

      {editing && params && (
        <>
          <div className="cst-name-row">
            <span className="cst-name">{editing.name}</span>
            <button
              type="button"
              className="cta cta-secondary cta-sm"
              onClick={() => {
                setNameValue(editing.name);
                setNameSheet('rename');
              }}
            >
              {t('app.cst.rename')}
            </button>
          </div>

          {options.sliders.map((spec) => (
            <Slider
              key={spec.key}
              spec={spec}
              value={params[spec.key]}
              label={t(`app.cst.slider.${spec.key}`)}
              display={valueLabel(t, spec.key, params[spec.key])}
              onChange={(value) => setParam(spec.key, value)}
            />
          ))}

          <div>
            <div className="label">{t('app.cst.mode')}</div>
            <div className="chip-grid" style={{ '--chip-cols': 4 } as React.CSSProperties}>
              {options.modes.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`chip ${params.mode === mode ? 'is-active' : ''}`}
                  onClick={() => {
                    haptic();
                    setParam('mode', mode);
                  }}
                >
                  <span className="chip-label">{t(`cst.mode.${mode}`)}</span>
                </button>
              ))}
            </div>
            <p className="step-hint">{t(`app.strategy.mode.${params.mode}`)}</p>
          </div>

          {options.indicators.length > 0 && (
            <div>
              <div className="label">{t('app.cst.indicators')}</div>
              <div className="cst-checks">
                {options.indicators.map((item) => {
                  const on = params.indicators.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`cst-check ${on ? 'is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleIndicator(item.id)}
                    >
                      <span className="cst-check-box">{on ? '✓' : ''}</span>
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="step-hint">{t('app.cst.indicators_hint')}</p>
            </div>
          )}

          <div className="cst-toggle-row">
            <div>
              <div className="label">{t('app.cst.news')}</div>
              <p className="step-hint">{t('app.cst.news_hint')}</p>
            </div>
            <button
              type="button"
              className={`atr-toggle cst-toggle ${params.newsFilter ? 'is-on' : ''}`}
              aria-pressed={params.newsFilter}
              onClick={() => {
                haptic();
                setParam('newsFilter', !params.newsFilter);
              }}
            />
          </div>

          {dirty && <p className="step-hint">{t('app.cst.unsaved')}</p>}
          <div className="cst-actions">
            <button type="button" className="cta cta-secondary" disabled={busy || !dirty} onClick={() => void save()}>
              {t('app.cst.save')}
            </button>
            <button
              type="button"
              className="cta cta-launch"
              disabled={busy || (editing.id === selectedId && !dirty)}
              onClick={() => void select()}
            >
              {editing.id === selectedId && !dirty ? t('app.cst.selected') : t('app.cst.select')}
            </button>
          </div>
          {confirmDelete ? (
            <div className="cst-actions">
              <button type="button" className="cta cta-secondary" disabled={busy} onClick={() => setConfirmDelete(false)}>
                {t('app.cst.cancel')}
              </button>
              <button type="button" className="cta cta-secondary cst-danger" disabled={busy} onClick={() => void remove()}>
                {t('app.cst.delete_confirm', { name: editing.name })}
              </button>
            </div>
          ) : (
            <button type="button" className="cta cta-secondary cta-block cst-danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
              {t('app.cst.delete')}
            </button>
          )}
        </>
      )}
    </div>
  );

  const selected = list.find((item) => item.id === selectedId) ?? null;
  const pillParams = selected?.params ?? params;
  const pills = pillParams
    ? {
        stake: `${pillParams.stakePercent} %`,
        series: `${pillParams.seriesCount} × ${pillParams.maxSteps}`,
        time: t('app.atr.minutes', { value: pillParams.durationMinutes }),
      }
    : { stake: '—', series: '—', time: '—' };

  return (
    <>
      <AutotradingPanel
        settings={settings}
        onSettings={onSettings}
        brandName={brandName}
        custom={{
          title: selected ? selected.name : t('app.cst.title'),
          subtitle: t('app.cst.subtitle'),
          pills,
          editor,
          costNote: t('app.cst.cost_note', { cost: seriesCost }),
          // без выбранного пресета запускать нечего (сервер ответил бы no_preset)
          canStart: selected !== null,
        }}
      />

      {nameSheet && (
        <SheetPortal>
          <div className="sheet-backdrop" onClick={() => !busy && setNameSheet(null)}>
            <div className="sheet" onClick={(event) => event.stopPropagation()}>
              <div className="sheet-handle" />
              <span className="kicker">{t('app.cst.title')}</span>
              <h2 className="card-title">{nameSheet === 'rename' ? t('app.cst.rename') : t('app.cst.new')}</h2>
              <div className="label">{t('app.cst.name_label')}</div>
              <input
                className="field"
                type="text"
                maxLength={options.limits.nameMaxLength}
                placeholder={t('app.cst.name_placeholder')}
                value={nameValue}
                autoFocus
                onChange={(event) => setNameValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void submitName();
                }}
              />
              <div className="row" style={{ marginTop: 16 }}>
                <button type="button" className="cta cta-secondary grow" disabled={busy} onClick={() => setNameSheet(null)}>
                  {t('app.cst.cancel')}
                </button>
                <button
                  type="button"
                  className="cta cta-launch grow"
                  disabled={busy || nameValue.trim().length === 0}
                  onClick={() => void submitName()}
                >
                  {nameSheet === 'rename' ? t('app.cst.save') : t('app.cst.create')}
                </button>
              </div>
            </div>
          </div>
        </SheetPortal>
      )}
    </>
  );
}
